import { FormCard } from "Components/Form/FormCard";
import { I18N_NS } from "Config/i18n";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { Button, Col, Empty, Row, Space, Statistic } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Bar, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { parseMcpOverviewWindow } from "./mcpAdminFormat";

const emptyOverview = (): API_McpAdminOverview => ({
  window: "7d",
  usersWithConnection: 0,
  distinctClientIds: 0,
  toolRows: 0,
  overrides: 0,
  admissionPolicy: "open",
  topTools: [],
  byUser: [],
});

const truncateTick =
  (clipId: string) =>
  ({ x, y, payload }: { x: number; y: number; payload: { value: string } }) =>
    (
      <g transform={`translate(${x},${y})`}>
        <defs>
          <clipPath id={clipId}>
            <rect x={-150} y={-10} width="150" height="18" />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          <title>{payload.value}</title>
          <text x={-150} y={-10} dy={12} textAnchor="start" fill="#666">
            {payload.value}
          </text>
        </g>
      </g>
    );

export const McpOverviewTab = () => {
  const { t } = useTranslation(I18N_NS.ADMIN);
  const [searchParams, setSearchParams] = useSearchParams();
  const windowKey = parseMcpOverviewWindow(searchParams.get("window"));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<API_McpAdminOverview>(emptyOverview());

  useEffect(() => {
    if (searchParams.get("window") === windowKey) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("window", windowKey);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, windowKey]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const overview = await API.getData<API_McpAdminOverview>(API_URL.MCP_ADMIN.getMcpOverview(), {
          window: windowKey,
        });
        if (!cancelled) {
          setData({ ...emptyOverview(), ...overview });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [windowKey]);

  const setWindow = (nextWindow: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("window", parseMcpOverviewWindow(nextWindow));
    setSearchParams(next, { replace: true });
  };

  const topTools = data.topTools ?? [];
  const byUser = (data.byUser ?? []).map((row) => ({
    ...row,
    label: row.userName ? `${row.userId} · ${row.userName}` : String(row.userId ?? ""),
  }));
  const toolTick = truncateTick("mcp-admin-tools");
  const userTick = truncateTick("mcp-admin-users");

  return (
    <div>
      <FormCard title={t("Overview")} loading={loading} className="mb-2">
        <fieldset className="mb-2" style={{ border: "none", minInlineSize: 0, padding: 0, margin: 0 }}>
          <legend className="mb-2">{t("Time range")}</legend>
          <Space wrap>
            <Button type={windowKey === "1d" ? "primary" : "default"} onClick={() => setWindow("1d")}>
              {t("Last 24 hours")}
            </Button>
            <Button type={windowKey === "7d" ? "primary" : "default"} onClick={() => setWindow("7d")}>
              {t("Last 7 days")}
            </Button>
            <Button type={windowKey === "30d" ? "primary" : "default"} onClick={() => setWindow("30d")}>
              {t("Last 30 days")}
            </Button>
            <Button type={windowKey === "all" ? "primary" : "default"} onClick={() => setWindow("all")}>
              {t("All time")}
            </Button>
          </Space>
        </fieldset>
        <p className="mb-2">{t("Data from PAVE heartbeat tables, not cluster quota.")}</p>
        <p className="mb-2">
          {t(
            "The time range filters connections and tool rows by last used. " +
              "Call counts are lifetime totals for those rows, " +
              "not calls only in the window. " +
              "Quota overrides and admission policy ignore the filter."
          )}
        </p>
        <Row gutter={16}>
          <Col xs={12} md={4}>
            <Statistic title={t("Users with a connection")} value={data.usersWithConnection ?? 0} />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title={t("Distinct clients")} value={data.distinctClientIds ?? 0} />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title={t("Tool rows")} value={data.toolRows ?? 0} />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title={t("Quota overrides")} value={data.overrides ?? 0} />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title={t("Admission policy")} value={data.admissionPolicy ?? "open"} />
          </Col>
        </Row>
      </FormCard>
      <FormCard title={t("Top tools by call count")} className="mb-2">
        {topTools.length === 0 ? (
          <Empty description={t("No heartbeat data yet")} />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, 80 + topTools.length * 28)}>
            <ComposedChart data={topTools} layout="vertical" margin={{ top: 20, right: 20, bottom: 20, left: 105 }}>
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" padding={{ top: 10, bottom: 10 }} tick={toolTick} />
              <Tooltip />
              <Bar dataKey="calls" barSize={20} fill="#8884d8" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </FormCard>
      <FormCard title={t("Calls by user")}>
        {byUser.length === 0 ? (
          <Empty description={t("No heartbeat data yet")} />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, 80 + byUser.length * 28)}>
            <ComposedChart data={byUser} layout="vertical" margin={{ top: 20, right: 20, bottom: 20, left: 105 }}>
              <XAxis type="number" />
              <YAxis dataKey="label" type="category" padding={{ top: 10, bottom: 10 }} tick={userTick} />
              <Tooltip />
              <Bar dataKey="calls" barSize={20} fill="#82ca9d" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </FormCard>
    </div>
  );
};
